/**
 * referralEntryViewModel — ce que les entrées du parrainage lisent dans le store : le bandeau en
 * bas de l'accueil et la ligne « Parrainer une famille » du portail Abonnement.
 *
 * Aucune donnée nouvelle, aucun appel : `currentUser.object` (`UserResource`) porte l'adresse du
 * tableau de bord et le gain du parrain sous la MÊME condition — `whenLoaded('referralCodeAsReferrer')`,
 * chargée par `User::$studentEagerLoading`. L'adresse est ce que lisait le cœur du header pour
 * décider s'il s'affichait (`ParentTabHeader.js:107`) ; le gain est le `commission_amount` que la
 * page de parrainage écrit « 50 € » (`referralRewardLabel`, la même fonction).
 *
 * Écart voulu avec la page : un gain nul ou illisible ne s'écrit PAS ici. La page le montre (« 0€ »,
 * c'est un compteur) ; un bandeau qui promettrait « 0€ pour vous » dirait le contraire de ce qu'il
 * propose. Il retombe alors sur sa phrase sans montant.
 *
 * Mémoïsé sur les deux valeurs et non sur `currentUser.object`, qui change d'identité à chaque
 * rechargement du compte (même raison que `parentShellViewModel`).
 */
import { createSelector } from "reselect"

import { referralRewardLabel } from "../../../adapters/parent/Referral/referralDashboardAdapter"
import { selectAccount } from "../../shared/displayedStudent"

export type ReferralEntryViewModel = {
    /** Le dernier segment de l'URL, tel que la route `referrer/dashboard` l'attend. Absent ⇒ pas d'entrée. */
    referralHash?: string
    /** « 50 € » — ce que gagne le parrain quand une famille invitée s'abonne. Absent ⇒ phrase sans montant. */
    rewardLabel?: string
}

// Le store est en JS non typé : on lit les deux champs qu'on connaît, sans le décrire en entier.
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

const selectReferralHash = (state: ReduxState): string | undefined =>
    referralHashOf(selectAccount(state)?.referrer_dashboard_url)

const selectRewardLabel = (state: ReduxState): string | undefined =>
    positiveRewardLabel(selectAccount(state)?.referral_commission_amount)

export const referralEntryViewModel = createSelector(
    [selectReferralHash, selectRewardLabel],
    (referralHash, rewardLabel): ReferralEntryViewModel => ({ referralHash, rewardLabel })
)

/** `https://…/r/ABC123` → `ABC123`, comme `ParentTabHeader.js:107-109`. */
export function referralHashOf(url: unknown): string | undefined {
    if (typeof url !== "string" || !url) return undefined
    return url.split("/").pop() || undefined
}

/** Le gain écrit comme la page (`referralRewardLabel`), seulement s'il est un montant positif. */
function positiveRewardLabel(amount: unknown): string | undefined {
    if (typeof amount !== "number" && typeof amount !== "string") return undefined
    const value = Number(amount)
    if (!Number.isFinite(value) || value <= 0) return undefined
    return referralRewardLabel(amount)
}
